/** Admin signal review: pricing can be edited only while the signal is pending. */
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import moment from 'jalali-moment';
import { toast } from "react-toastify";

import { commafy, decommafy, swal, toEnglishDigits } from "../../../utils/methods";
import { _post } from "../../../utils/network";

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from "../../../components/Form/FormField/FormField.component";
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';

/** Review a signal and save pending pricing together with its approval status. */
const SignalsCreate = ({ componentType='create' }) => {
	const { t } = useTranslation();
	const { id } = useParams();
	const [signal, setSignal] = useState({});
	const [signalFile, setSignalFile] = useState(null);
	const [isSignalImageUploading, setIsSignalImageUploading] = useState(false);
	const [priceUnit, setPriceUnit] = useState('sim_zar');
	const canEditPricing = signal.signal_state === 0;
	const priceUnitsList = [
		{ key: t('score2_name'), value: 'zar' },
		{ key: `${t('score1_name')} ${t('and')} ${t('score2_name')}`, value: 'sim_zar' }
	];
	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('signals'), link: '/signals-list' },
		{ title: t(componentType) }
	];
	const confirmSwal = statements => {
		swal(() => statements(), {
			text: t('are_you_sure_to_do_this'),
			confirmButtonText: t('yes'),
			cancelButtonText: t('no')
		});
	};

	const [createButtonIsLoading, setCreateButtonIsLoading] = useState(false);
	/** Validate the review and include pricing only for a pending signal. */
	const createSignal = async (event) => {
		event.preventDefault();
		const form = event.currentTarget;
		if(createButtonIsLoading || isSignalImageUploading || signal.signal_state === undefined)
			return 0;
		let status = parseInt(form.elements.namedItem('signalStatus').value);
		switch(signal.signal_state){
			case 0:
				if(![0, 1, 4].includes(status))
					return toast.warn(t('select_status_correctly'), 'warning');
				break;
			case 1:
				if(![1, 6].includes(status))
					return toast.warn(t('select_status_correctly'), 'warning');
				break;
			case 2:
				if(![2, 6].includes(status))
					return toast.warn(t('select_status_correctly'), 'warning');
				break;
			case 3:
				if(![3].includes(status))
					return toast.warn(t('select_status_correctly'), 'warning');
				break;
			case 4:
				if(![4].includes(status))
					return toast.warn(t('select_status_correctly'), 'warning');
				break;
			case 5:
				if(![5].includes(status))
					return toast.warn(t('select_status_correctly'), 'warning');
				break;
			case 6:
				if(![6].includes(status))
					return toast.warn(t('select_status_correctly'), 'warning');
				break;
			default:
				return toast.warn(t('select_status_correctly'), 'warning');
		}
		let body = {
			signal_id: id,
			signal_state: status
		};
		if(componentType === 'edit' && signalFile !== (signal.signal_information?.file || null))
			body.signal_file = signalFile;
		if(canEditPricing){
			// Normalize local digits before validating; parseInt would silently accept decimals or trailing text.
			const priceText = toEnglishDigits(decommafy(form.elements.namedItem('signalPrice').value.trim())).replaceAll('٬', '');
			const price = Number(priceText);
			if(!/^\d+$/.test(priceText) || !Number.isInteger(price) || price > 2147483647)
				return toast.warn(t('enter_price_correctly'));
			const selectedPriceUnit = form.elements.namedItem('signalPriceUnit').value;
			if(!priceUnitsList.some(item => item.value === selectedPriceUnit))
				return toast.warn('واحد هزینه را صحیح انتخاب کنید.');
			body.signal_price = price;
			body.price_unit = selectedPriceUnit;
		}
		confirmSwal(async () => {
			setCreateButtonIsLoading(true);
			try {
				const response = await _post('/signals/admin/' + ((componentType === 'edit') ? 'editState' : componentType), body);
				if(response?.code !== 200){
					toast.error(response?.[t('api_message')] || 'ذخیرهٔ تغییرات سیگنال ممکن نشد.');
					// Another reviewer may have already approved it; refresh the fields instead of retaining stale permissions.
					if(response?.code === 409)
						await getSignal();
					return;
				}
				toast.success(response[t('api_message')]);
				setSignal(previous => {
					const updatedSignalInformation = { ...(previous.signal_information || {}) };
					if(signalFile)
						updatedSignalInformation.file = signalFile;
					else
						delete updatedSignalInformation.file;
					if(canEditPricing)
						updatedSignalInformation.price_unit = body.price_unit;
					return {
						...previous,
						signal_state: status,
						...(canEditPricing ? { signal_price: body.signal_price } : {}),
						signal_information: updatedSignalInformation
					};
				});
				if(componentType === 'create')
					form.reset();
			} catch {
				toast.error('ذخیرهٔ تغییرات سیگنال ممکن نشد.');
			} finally {
				setCreateButtonIsLoading(false);
			}
		});
	}
	
	/** Load the saved price unit, including the fallback for legacy signals. */
	const getSignal = useCallback(async (isCurrent = () => true) => {
		let body = {
			signal_id: id
		};
		let response = await _post('/signals/admin/getOne', body);
		if(!isCurrent())
			return;
		switch(response?.code){
			case 200:
				setSignal(response.data.signal);
				setSignalFile(response.data.signal.signal_information?.file || null);
				setPriceUnit(response.data.signal.signal_information?.price_unit === 'zar' ? 'zar' : 'sim_zar');
				break;
			default:
				toast.error(response?.[t('api_message')] || 'دریافت اطلاعات سیگنال ممکن نشد.');
		}
	}, [id, t]);

	const signalStatusList = [
		{ key: t('pending'), value: 0, disabled: ![0].includes(signal?.signal_state) },
		{ key: t('confirmed'), value: 1, disabled: ![0, 1].includes(signal?.signal_state) },
		{ key: t('active'), value: 2, disabled: ![2].includes(signal?.signal_state) },
		{ key: t('closed'), value: 3, disabled: ![3].includes(signal?.signal_state) },
		{ key: t('failed'), value: 4, disabled: ![0, 4].includes(signal?.signal_state) },
		{ key: t('canceled'), value: 5, disabled: ![5].includes(signal?.signal_state) },
		{ key: t('canceled_after_confirmation'), value: 6, disabled: ![1, 2, 6].includes(signal?.signal_state) }
	];

	useEffect(() => {
		let isCurrent = true;
		setSignal({});
		setSignalFile(null);
		setPriceUnit('sim_zar');
		if(componentType === 'edit')
			getSignal(() => isCurrent);
		// Ignore a previous signal's response when this route changes or unmounts.
		return () => { isCurrent = false; };
	}, [componentType, getSignal]);

	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t(componentType)} {t('signal')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={createSignal}>
						<div className="row gx-2">
							<FormField fieldKey="signalCreationDate" title={t('creation_date')} value={signal?.create_date ? moment(signal?.create_date?.substring(0, 10), 'YYYY-MM-DD').locale('fa').format('jYYYY/jMM/jDD') : ''} disabled ltr />
							<FormField fieldKey="signalUser" title={t('user')} value={signal?.user_id_information?.user_fullname} disabled rtl />
							<FormField fieldKey="signalStock" title={t('stock')} value={signal?.stock_id_information?.stock_information?.stock_symbol} disabled rtl />
							<FormField fieldKey="signalStartPoint" title={t('start_point')} value={commafy(signal?.start_point)} disabled ltr />
							<FormField fieldKey="signalEndPoint" title={t('end_point')} value={commafy(signal?.profit_point)} disabled ltr />
							<FormField fieldKey="signalLossPoint" title={t('loss_point')} value={commafy(signal?.loss_point)} disabled ltr />
							<FormField key={`${signal.signal_state}-${signal.signal_price}`} type="number" fieldKey="signalPrice" title={t('price')} tip={priceUnit === 'zar' ? t('score2_name') : t('score1_name')} value={commafy(signal?.signal_price)} disabled={!canEditPricing || createButtonIsLoading} required ltr />
							<FormField type="select" fieldKey="signalPriceUnit" title={t('price_unit')} value={priceUnit} required selectItems={priceUnitsList} disabled={!canEditPricing || createButtonIsLoading} selectOnChange={event => setPriceUnit(event.target.value)} />
							<FormField type="image" fieldKey="signalImage" title="تصویر سیگنال" value={signalFile} fileMaxSize={1} uploadContext="signal" disabled={createButtonIsLoading || isSignalImageUploading || signal.signal_state === undefined} onUploadChange={setSignalFile} onUploadLoadingChange={setIsSignalImageUploading} />
							<FormField type="select" fieldKey="signalStatus" title={t('status')} value={signal?.signal_state} required selectItems={signalStatusList} disabled={createButtonIsLoading || signal.signal_state === undefined} />
						</div>
						<p className="text-muted small">ویرایش مقدار و واحد هزینه فقط در وضعیت «در انتظار تأیید» امکان‌پذیر است.</p>
						<div>
							{componentType === 'create' ?
								<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="create" isLoading={createButtonIsLoading} disabled={createButtonIsLoading || isSignalImageUploading || signal.signal_state === undefined}>
									<i className="fa fa-save"></i> {t('create')}
								</LoadingButton>
							:
								<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="edit" isLoading={createButtonIsLoading} disabled={createButtonIsLoading || isSignalImageUploading || signal.signal_state === undefined}>
									<i className="fa fa-save"></i> {t('update')}
								</LoadingButton>
							}
						</div>
					</form>
				</div>
			</div>
		</>
	);
};

export default SignalsCreate;
